/**
 * ProofEvidencePanel — calm progression display.
 *
 * Shows advisory AI/video evidence counts with a soft progress bar.
 * Renders evidence rows underneath, marked with the three-state
 * verdict vocabulary: proof_verified / building_proof / not_yet_proven.
 *
 * iter_158b additions (Feb 13, 2026): retained-foundation regression
 * rendering. Each evidence row may carry `retained_regressions[]`
 * (responsibility_id + severity + coaching + homework_link). Severity
 * pills + "Review card" deep-links surface the calm coaching message
 * — "Strong work on the focus layer, but [foundation] slipped this
 * time. Practise both together."
 *
 * Locked principle: NEVER show harsh pass/fail. Render as a portfolio
 * the learner is building, not a test they are taking.
 */
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, AlertCircle, MapPin, ArrowRight } from "lucide-react";

const VERDICTS = {
  proof_verified: {
    label: "AI observation · advisory",
    Icon: CheckCircle2,
    classes: "text-emerald-700 bg-emerald-50 border-emerald-200",
  },
  building_proof: {
    label: "Building advisory evidence",
    Icon: Circle,
    classes: "text-amber-700 bg-amber-50 border-amber-200",
  },
  not_yet_proven: {
    label: "More evidence needed",
    Icon: AlertCircle,
    classes: "text-slate-600 bg-slate-50 border-slate-200",
  },
};

const SEVERITY_TONE = {
  minor:    "bg-yellow-50 text-yellow-800 border-yellow-200",
  moderate: "bg-orange-50 text-orange-800 border-orange-200",
  severe:   "bg-rose-50 text-rose-800 border-rose-200",
};


export default function ProofEvidencePanel({ verified, required, status, evidence }) {
  const nav = useNavigate();
  const pct = required > 0 ? Math.min(100, Math.round((verified / required) * 100)) : 0;
  const s = VERDICTS[status] || VERDICTS.not_yet_proven;
  const { Icon } = s;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5"
         data-testid="proof-evidence-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-bold tracking-[0.12em] text-slate-500 mb-0.5">
            ADVISORY VIDEO EVIDENCE
          </div>
          <div className="text-base font-semibold text-slate-900"
               data-testid="proof-evidence-counts">
            {verified} of {required} AI/video observations for coaching
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${s.classes}`}
             data-testid="proof-evidence-status">
          <Icon size={13} />
          <span className="text-xs font-semibold">{s.label}</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden"
           data-testid="proof-evidence-progress">
        <div
          className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>

      {evidence && evidence.length > 0 && (
        <div className="mt-5">
          <div className="text-xs font-semibold text-slate-600 mb-2">
            Recent evidence
          </div>
          <ul className="space-y-2"
              data-testid="proof-evidence-list">
            {evidence.slice(0, 5).map((e, i) => {
              const v = VERDICTS[e.verdict] || VERDICTS.not_yet_proven;
              const Vicon = v.Icon;
              const regressions = e.retained_regressions || [];
              const retentionBlocked = !!e.retention_blocked;
              return (
                <li key={e.id || i}
                    className="p-3 rounded-xl bg-slate-50 border border-slate-100"
                    data-testid={`proof-evidence-item-${i}`}>
                  <div className="flex items-start gap-2">
                    <Vicon size={16} className={v.classes.split(" ")[0]} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-800">
                        {v.label}
                        {e.variety_blocked && (
                          <span className="ml-2 text-[11px] font-semibold text-amber-700 uppercase tracking-wide">
                            variety needed
                          </span>
                        )}
                        {retentionBlocked && (
                          <span
                            className="ml-2 text-[11px] font-semibold text-rose-700 uppercase tracking-wide"
                            data-testid={`proof-evidence-retention-blocked-${i}`}
                          >
                            foundation slipped
                          </span>
                        )}
                      </div>
                      {e.coaching && (
                        <div className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                          {e.coaching}
                        </div>
                      )}
                      {e.location_fingerprint && e.location_fingerprint !== "unknown" && (
                        <div className="flex items-center gap-1 text-[11px] text-slate-500 mt-1">
                          <MapPin size={10} />
                          <span className="truncate">{e.location_fingerprint}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {regressions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200"
                         data-testid={`proof-evidence-regressions-${i}`}>
                      <div className="text-[11px] font-bold tracking-[0.1em] text-slate-500 mb-2">
                        FOUNDATION SLIPS
                      </div>
                      <ul className="space-y-1.5">
                        {regressions.map((r, j) => (
                          <li
                            key={j}
                            className={`flex items-center justify-between gap-2 p-2 rounded-lg border ${SEVERITY_TONE[r.severity] || SEVERITY_TONE.minor}`}
                            data-testid={`proof-regression-row-${i}-${j}`}
                            data-severity={r.severity}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold capitalize">
                                {r.responsibility_id?.replaceAll("_", " ") || "—"}
                                <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide opacity-80">
                                  · {r.severity || "minor"}
                                </span>
                              </div>
                              {r.coaching && (
                                <div className="text-[11px] mt-0.5 leading-snug opacity-90">
                                  {r.coaching}
                                </div>
                              )}
                            </div>
                            {r.homework_link?.hub_path && (
                              <button
                                onClick={() => nav(r.homework_link.hub_path)}
                                className="flex-shrink-0 px-2 py-1 rounded-md text-[11px] font-semibold bg-white/80 hover:bg-white border border-current/30 inline-flex items-center gap-1"
                                data-testid={`proof-regression-homework-${i}-${j}`}
                              >
                                Review card
                                <ArrowRight size={11} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="text-[11px] text-slate-500 mt-4 leading-relaxed">
        Same location counts max 2× toward advisory evidence. These observations
        support learning and instructor review; they do not establish competence.
      </div>
    </div>
  );
}
